import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Sidebar from './Sidebar';
import { Session } from "./utils/session";
import { API_BASE_URL } from '../config';


// --- FIX FOR REACT 18 STRICT MODE ---
export const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};
// ------------------------------------

// Define the structure for a single task column (list)
const initialData = {
  tasks: {
    'task-1': { id: 'task-1', content: 'Brainstorm new feature ideas for the dashboard.', image: null },
    'task-2': { id: 'task-2', content: 'Review and approve the latest marketing campaign assets.', image: null },
    'task-3': { id: 'task-3', content: 'Fix navigation bug on mobile.', image: null },
  },
  columns: {
    'employee-1': { id: 'employee-1', title: 'Employee-1', taskIds: ['task-1', 'task-2'], finishedTaskIds: [] },
    'employee-2': { id: 'employee-2', title: 'Employee-2', taskIds: ['task-3'], finishedTaskIds: [] },
    'employee-3': { id: 'employee-3', title: 'Employee-3', taskIds: [], finishedTaskIds: [] },
    'employee-4': { id: 'employee-4', title: 'Employee-4', taskIds: [], finishedTaskIds: [] },
    'employee-5': { id: 'employee-5', title: 'Employee-5', taskIds: [], finishedTaskIds: [] },
    'employee-6': { id: 'employee-6', title: 'Employee-6', taskIds: [], finishedTaskIds: [] },
    'employee-7': { id: 'employee-7', title: 'Employee-7', taskIds: [], finishedTaskIds: [] },
  },
  columnOrder: ['employee-1', 'employee-2', 'employee-3', 'employee-4', 'employee-5', 'employee-6', 'employee-7'],
};

const KanbanCard = ({ task, index, onCardClick, isDragDisabled }) => {
  return (
    <Draggable draggableId={task.id} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          className={`kanban-card p-3 rounded-lg shadow-sm border mb-2 
            ${isDragDisabled ? 'bg-light text-muted border-success' : 'bg-white'} 
            ${snapshot.isDragging ? 'shadow-lg' : ''}`}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          onClick={() => onCardClick(task)}
          style={{ ...provided.draggableProps.style, opacity: isDragDisabled ? 0.8 : 1 }}
        >
          {isDragDisabled && (
             <div className="d-flex align-items-center text-success mb-2 small fw-bold">
                 <i className="fas fa-check-circle me-1"></i> COMPLETED
             </div>
          )}
          {task.image && (
            <img src={task.image} alt="Task attachment" className="card-img-top mb-2 rounded" />
          )}
          <p className="mb-2" style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>{task.content}</p>
          {!isDragDisabled && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="kanban-card-tags">
                <span className="badge bg-primary-subtle text-primary-emphasis me-1">UI/UX</span>
                <span className="badge bg-success-subtle text-success-emphasis">Research</span>
              </div>
              <img 
                src="https://i.pravatar.cc/24" 
                alt="User avatar" 
                className="rounded-circle"
              />
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

const KanbanColumn = ({ column, tasks, finishedTasks, onAddTask, onCardClick }) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardText, setNewCardText] = useState('');
  const [newCardImage, setNewCardImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = React.useRef(null);
  const role = Session.getRole();
  const isMember = role === "Member";

  const handleAddClick = () => {
    if (newCardText.trim()) {
      onAddTask(column.id, newCardText.trim(), newCardImage);
      setNewCardText('');
      setNewCardImage(null);
      setImagePreview('');
      setIsAddingCard(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewCardImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <div className="kanban-column-wrapper d-flex flex-column h-100">
        <div className="bg-light p-3 rounded-3 shadow-sm border-top border-primary border-3 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                <h3 className="h6 fw-bold mb-0 me-2">{column.title}</h3>
                <span className="badge bg-dark-subtle text-dark-emphasis rounded-pill">{tasks.length}</span>
                </div>
                <div className="kanban-column-controls">
                <button className="btn btn-sm btn-light">
                    <i className="fas fa-ellipsis-h" />
                </button>
                </div>
            </div>
            
            {/* Cards list - Using StrictModeDroppable here */}
            <StrictModeDroppable droppableId={column.id} type="task">
                {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="kanban-card-list mt-3"
                >
                    {tasks.map((task, index) => <KanbanCard key={task.id} task={task} index={index} onCardClick={onCardClick} isDragDisabled={false} />)}
                    {provided.placeholder}
                </div>
                )}
            </StrictModeDroppable>
            
            {/* --- FINISHED TASK DROP ZONE (Conditional Rendering) --- */}
            {(tasks.length > 0 || finishedTasks.length > 0) && (
                <div className="kanban-finish-zone mt-3 pt-3 border-top">
                    <h6 className="text-muted small fw-bold text-uppercase d-flex align-items-center">
                        <i className="fas fa-check-circle me-2"></i>
                        Finished Tasks
                    </h6>
                    <StrictModeDroppable droppableId={`${column.id}-finish`} type="task">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`finish-box mt-2 p-2 rounded border border-dashed 
                                    ${snapshot.isDraggingOver ? 'bg-success-subtle border-success' : 'bg-light'}`}
                                style={{ minHeight: '80px', transition: 'background-color 0.2s ease' }}
                            >
                                {finishedTasks.map((task, index) => (
                                    <KanbanCard key={task.id} task={task} index={index} onCardClick={onCardClick} isDragDisabled={true} />
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </StrictModeDroppable>
                </div>
            )}

            {/* New Card Form */}
            {isAddingCard ? (
                <div className="new-card-form mt-2">
                <div className="kanban-card bg-white p-3 rounded-lg shadow-sm border mb-2">
                    {imagePreview && (
                    <img src={imagePreview} alt="Preview" className="card-img-top mb-2 rounded" />
                    )}
                    <textarea
                    className="form-control border-0 p-0"
                    rows="3"
                    placeholder="Enter a title for this card..."
                    value={newCardText}
                    onChange={(e) => setNewCardText(e.target.value)}
                    autoFocus
                    ></textarea>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <button className="btn btn-sm btn-light" onClick={() => fileInputRef.current.click()}>
                            <i className="fas fa-paperclip me-1" /> Attach image
                        </button>
                        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} className="d-none" />
                        <img src="https://i.pravatar.cc/24" alt="User avatar" className="rounded-circle"/>
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <button className="btn btn-primary btn-sm" onClick={handleAddClick}>Add card</button>
                    <button className="btn btn-light btn-sm ms-2" onClick={() => { setIsAddingCard(false); setImagePreview(''); setNewCardImage(null); }}>
                    <i className="fas fa-times" />
                    </button>
                </div>
                </div>
            ) : (
               !isMember && (
                <button 
                className="kanban-add-card btn btn-outline-secondary w-100 border-dashed py-2 d-flex align-items-center justify-content-center mt-2"
                onClick={() => setIsAddingCard(true)}
                >
                <i className="fas fa-plus me-2" />
                <span>Add a card</span>
                </button> )
            )}
        </div>
    </div>
  );
};

// --- THIS IS THE DEFAULT EXPORT THAT WAS MISSING ---
export default function TaskPage() {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [boardData, setBoardData] = useState(initialData);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [users, setUsers] = useState([]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    } else {
      setHideDesktop((s) => !s);
    }
  };

  const buildBoardFromUsers = (users) => {
  const columns = {};
  const columnOrder = [];

  users.forEach(user => {
    const columnId = `user-${user.userId}`;

    columns[columnId] = {
      id: columnId,
      title: `${user.firstName} ${user.lastName}`,
      taskIds: [],
      finishedTaskIds: []
    };

    columnOrder.push(columnId);
  });

  setBoardData(prev => ({
    ...prev,
    columns,
    columnOrder
  }));
};

  useEffect(() => {
  fetch(`${API_BASE_URL}/api/Members/Users_List`)
    .then(res => res.json())
    .then(data => {
      setUsers(data);
      buildBoardFromUsers(data);
    setTimeout(() => {
        loadCards();
      }, 200);
    });
}, []);

const loadCards = async () => {
  try {
    const res = await fetch("https://localhost:7121/api/Cards/list");
    if (!res.ok) throw new Error("Failed to load cards");

    const cards = await res.json();

    setBoardData(prev => {
      const tasks = {};
      const columns = { ...prev.columns };

      // clear old taskIds
      Object.keys(columns).forEach(colId => {
        columns[colId].taskIds = [];
      });

      cards.forEach(card => {
        const taskId = `task-${card.cardID}`;
        const columnId = `user-${card.assignedUserID}`;

        tasks[taskId] = {
          id: taskId,
          content: card.cardTitle,
          image: null
        };

        if (columns[columnId]) {
          columns[columnId].taskIds.push(taskId);
        }
      });

      return {
        ...prev,
        tasks,
        columns
      };
    });

  } catch (err) {
    console.error(err);
  }
};


  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setShowMobileSidebar(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
    if (selectedTask) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    };
  }, [selectedTask]);
  const handleCardClick = (task) => {
    setSelectedTask(task);
    setIsEditing(false); 
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    setIsEditing(false); 
  };

  const handleEditClick = () => {
    if (selectedTask) {
      setEditedContent(selectedTask.content);
      setIsEditing(true);
    }
  };

  const handleSaveClick = () => {
    if (!selectedTask) return;

    const updatedTasks = {
      ...boardData.tasks,
      [selectedTask.id]: {
        ...boardData.tasks[selectedTask.id],
        content: editedContent,
      },
    };

    setBoardData({ ...boardData, tasks: updatedTasks });

    setSelectedTask(prev => ({ ...prev, content: editedContent }));

    setIsEditing(false);
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleConfirmDelete = () => {
    if (!taskToDelete) return;

    setBoardData((prevData) => {
      const newTasks = { ...prevData.tasks };
      delete newTasks[taskToDelete];

      const newColumns = { ...prevData.columns };
      Object.keys(newColumns).forEach((columnId) => {
        const column = newColumns[columnId];
        const taskIds = column.taskIds.filter((id) => id !== taskToDelete);
        const finishedTaskIds = column.finishedTaskIds.filter((id) => id !== taskToDelete);
        newColumns[columnId] = { ...column, taskIds, finishedTaskIds };
      });
      return {
        ...prevData,
        tasks: newTasks,
        columns: newColumns,
      };
    });

    handleCloseModal(); 
    setShowDeleteConfirm(false); 
    setTaskToDelete(null);
  };

  const handleDeleteClick = (taskId) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirm(true);
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleAddTask = async (columnId, taskContent, taskImage) => {
  const assignedUserId = parseInt(columnId.replace("user-", ""), 10);

  const payload = {
    listID: 1,
    boardID: 1,
    cardTitle: taskContent,
    description: taskContent,
    position: 1,
    color: "#20c997",
    dueDate: new Date().toISOString(),
    createdUserID: 1,
    assignedUserID: assignedUserId
  };

  try {
    const res = await fetch(`${API_BASE_URL}/api/Cards/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) throw new Error("Failed to create card");

    const data = await res.json();

    const newTaskId = `task-${data.cardID}`;
    const newTask = {
      id: newTaskId,
      content: taskContent,
      image: null
    };

    setBoardData(prev => {
      const column = prev.columns[columnId];

      return {
        ...prev,
        tasks: {
          ...prev.tasks,
          [newTaskId]: newTask
        },
        columns: {
          ...prev.columns,
          [columnId]: {
            ...column,
            taskIds: [...column.taskIds, newTaskId]
          }
        }
      };
    });

  } catch (err) {
    console.error(err);
    alert("Card create failed");
  }
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const isMovingToFinish = destination.droppableId.includes('-finish');
    const isMovingFromFinish = source.droppableId.includes('-finish');

    if (isMovingFromFinish) {
        return; // Card ko finish zone se bahar nahi nikal sakte
    }

    const startColId = source.droppableId;
    const finishColId = destination.droppableId.replace('-finish', '');
    
    const startCol = boardData.columns[startColId];
    const finishCol = boardData.columns[finishColId];

    // --- Case 1: Moving within the same column (reordering or moving to its own finish zone) ---
    if (startCol === finishCol) {
        if (isMovingToFinish) {
            // Move from active list to finished list
            const newTaskIds = Array.from(startCol.taskIds);
            newTaskIds.splice(source.index, 1);
            
            const newFinishedTaskIds = Array.from(startCol.finishedTaskIds);
            newFinishedTaskIds.splice(destination.index, 0, draggableId);

            const newColumn = { ...startCol, taskIds: newTaskIds, finishedTaskIds: newFinishedTaskIds };
            setBoardData(prev => ({ ...prev, columns: { ...prev.columns, [newColumn.id]: newColumn } }));
        } else {
            // Reorder in the active list
            const newTaskIds = Array.from(startCol.taskIds);
            newTaskIds.splice(source.index, 1);
            newTaskIds.splice(destination.index, 0, draggableId);
            const newColumn = { ...startCol, taskIds: newTaskIds };
            setBoardData(prev => ({ ...prev, columns: { ...prev.columns, [newColumn.id]: newColumn } }));
        }
        return;
    }

    // --- Case 2: Moving between different columns ---
    const startTaskIds = Array.from(startCol.taskIds);
    startTaskIds.splice(source.index, 1);
    const newStart = { ...startCol, taskIds: startTaskIds };

    if (isMovingToFinish) {
        // Move from Active List (Col A) to Finished List (Col B)
        const finishTaskIds = Array.from(finishCol.finishedTaskIds);
        finishTaskIds.splice(destination.index, 0, draggableId);
        const newFinish = { ...finishCol, finishedTaskIds: finishTaskIds };
        setBoardData(prev => ({
            ...prev,
            columns: {
                ...prev.columns,
                [newStart.id]: newStart,
                [newFinish.id]: newFinish,
            },
        }));
    } else {
        // Move from Active List (Col A) to Active List (Col B)
        const finishTaskIds = Array.from(finishCol.taskIds);
        finishTaskIds.splice(destination.index, 0, draggableId);
        const newFinish = { ...finishCol, taskIds: finishTaskIds };
        setBoardData(prev => ({
            ...prev,
            columns: {
                ...prev.columns,
                [newStart.id]: newStart,
                [newFinish.id]: newFinish,
            },
        }));
    }
  };

  return (
    <div>
        <div id="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
              <Sidebar />
                <div id="content">
        <div className="d-flex align-items-center mb-3">
            <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
            </button>
            <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-4 page-header flex-wrap gap-2">
            <h2>Task</h2>
            <div className="top-icons">
            <button className="btn btn-sm rounded"><i className="fa-solid fa-arrow-up-right-from-square" /></button>
            <button className="btn btn-sm rounded"><i className="fa-solid fa-gear" /></button>
            </div>
        </div>
        
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="kanban-board d-flex gap-3 overflow-auto">
            {boardData.columnOrder.map(columnId => {
              const column = boardData.columns[columnId];
              const tasks = column.taskIds.map(taskId => boardData.tasks[taskId]);
              const finishedTasks = column.finishedTaskIds.map(taskId => boardData.tasks[taskId]);
              return (
                <div key={column.id} className="kanban-column">
                    <KanbanColumn 
                        column={column} 
                        tasks={tasks} 
                        finishedTasks={finishedTasks}
                        onAddTask={handleAddTask} 
                        onCardClick={handleCardClick} 
                    />
                </div>
              );
            })}
          </div>
        </DragDropContext>
        
        {/* Task Detail Modal */}
        {selectedTask && (
            <>
                <div className="modal-backdrop fade show"></div>
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" onClick={handleCloseModal}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-light border-0 p-4">
                                <div className="d-flex align-items-center">
                                    <span className="p-2 bg-primary-subtle text-primary-emphasis rounded-3 me-3">
                                        <i className="fas fa-tasks fa-lg" />
                                    </span>
                                    <div>
                                        {isEditing ? (
                                            <h5 className="modal-title mb-0 fw-bold">Editing Task</h5>
                                        ) : (
                                            <>
                                                <h5 className="modal-title mb-0 fw-bold">{selectedTask.content.substring(0, 50)}{selectedTask.content.length > 50 ? '...' : ''}</h5>
                                                <small className="text-muted">in list: <span className="fw-semibold">{boardData.columns[Object.values(boardData.columns).find(col => col.taskIds.includes(selectedTask.id) || col.finishedTaskIds.includes(selectedTask.id))?.id]?.title}</span></small>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body p-4">
                                {isEditing ? (
                                    <div>
                                        <label htmlFor="task-description" className="form-label fw-bold text-muted text-uppercase small">Description</label>
                                        <textarea 
                                            id="task-description"
                                            className="form-control" 
                                            rows="4" 
                                            value={editedContent}
                                            onChange={(e) => setEditedContent(e.target.value)}
                                        ></textarea>
                                    </div>
                                ) : (
                                    <div className="mb-4">
                                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Description</h6>
                                        <p>{selectedTask.content}</p>
                                    </div>
                                )}

                                {selectedTask.image && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Attachment</h6>
                                        <img src={selectedTask.image} alt="Task attachment" className="img-fluid rounded border" />
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Assigned To</h6>
                                        <div className="d-flex align-items-center">
                                            <img src="https://i.pravatar.cc/24" alt="User avatar" className="rounded-circle me-2"/>
                                            <span className="fw-semibold">User Name</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Tags</h6>
                                        <span className="badge bg-primary-subtle text-primary-emphasis me-1">UI/UX</span>
                                        <span className="badge bg-success-subtle text-success-emphasis">Research</span>
                                    </div>
                                </div>
                            </div>
                            {isEditing ? (
                                <div className="modal-footer bg-light border-0">
                                    <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>Cancel</button>
                                    <button type="button" className="btn btn-primary" onClick={handleSaveClick}>
                                        <i className="fas fa-save me-1"></i> Save Changes
                                    </button>
                                </div>
                            ) : (
                                <div className="modal-footer bg-light border-0">
                                    <button type="button" className="btn btn-outline-secondary" onClick={handleEditClick}><i className="fas fa-edit me-1"></i> Edit</button><button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteClick(selectedTask.id)}><i className="fas fa-trash me-1"></i> Delete</button>
                                    <button type="button" className="btn btn-primary" onClick={handleCloseModal}>Close</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        )}
        {showDeleteConfirm && (
            <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
                <div className="modal fade show" style={{ display: 'block', zIndex: 1065 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-body p-4 text-center">
                                <div className="mb-3">
                                    <i className="fas fa-exclamation-triangle fa-3x text-danger"></i>
                                </div>
                                <h5 className="fw-bold">Are you sure?</h5>
                                <p className="text-muted">Do you really want to delete this task? This process cannot be undone.</p>
                            </div>
                            <div className="modal-footer border-0 bg-light justify-content-center">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelDelete}>Cancel</button>
                                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Yes, Delete</button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )}
    </div>
    </div>
   
  );
}